import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import { PACKAGES } from "../../data/pricing";
import { useCountUp } from "../../hooks/useCountUp";

const CTA_LINK = "/#contact";

const Price = ({ pkg }) => {
  const ref = useRef(null);
  const count = useCountUp(pkg.price, ref);
  return (
    <div ref={ref} className="flex items-baseline gap-1 justify-center">
      <span className="font-title font-semibold text-2xl sm:text-3xl text-white/60 leading-none">£</span>
      <span
        data-testid={`package-price-${pkg.id}`}
        className="font-title font-semibold text-5xl sm:text-6xl text-white leading-none tracking-tight tabular-nums"
      >
        {count.toLocaleString()}
      </span>
    </div>
  );
};

const PackageCard = ({ pkg, index }) => (
  <motion.article
    data-testid={`package-card-${pkg.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    className={`relative flex flex-col h-full rounded-[22px] border p-7 sm:p-8 ${
      pkg.highlight
        ? "border-white/25 bg-[#0c0c0e] shadow-[0_50px_140px_-50px_rgba(255,255,255,0.14)] lg:scale-[1.03] z-10"
        : "border-white/[0.08] bg-white/[0.015]"
    }`}
  >
    {pkg.badge && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span
          data-testid="package-badge"
          className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-black text-[10px] tracking-[0.18em] uppercase font-heading font-medium shadow-lg whitespace-nowrap"
        >
          <Sparkles size={11} className="fill-black" /> {pkg.badge}
        </span>
      </div>
    )}

    <div className="text-center">
      <p className="font-mono-grotesk text-[10.5px] tracking-[0.3em] uppercase text-white/45 mb-5">
        {pkg.name}
      </p>
      <Price pkg={pkg} />
      <p className="mt-5 text-[13.5px] text-white/55 leading-relaxed min-h-[60px]">{pkg.tagline}</p>
    </div>

    <div className="my-6 h-px w-full bg-white/[0.08]" />

    <ul className="space-y-3 flex-1">
      {pkg.features.map((feat, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-0.5 grid place-items-center h-4.5 w-4.5 rounded-full bg-white/90 text-black shrink-0 p-[3px]">
            <Check size={10} strokeWidth={3} />
          </span>
          <span className="text-[13.5px] text-white/75 font-heading leading-snug">{feat}</span>
        </li>
      ))}
    </ul>

    <a
      href={CTA_LINK}
      data-testid={`package-cta-${pkg.id}`}
      className={`group/btn mt-8 inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 text-[13.5px] font-medium transition-all duration-300 hover:-translate-y-0.5 ${
        pkg.highlight
          ? "bg-white text-black hover:bg-white/90"
          : "border border-white/15 text-white hover:border-white/40 hover:bg-white/[0.06]"
      }`}
    >
      {pkg.cta}
      <ArrowUpRight size={15} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
    </a>
  </motion.article>
);

export const PricingCards = () => (
  <section data-testid="pricing-cards-section" className="relative py-12 sm:py-16 lg:py-20 bg-[#09090b]">
    <div className="mx-auto w-full max-w-[1200px] px-[5vw]">
      <div className="grid md:grid-cols-3 gap-5 lg:gap-6 items-stretch">
        {PACKAGES.map((pkg, i) => (
          <PackageCard key={pkg.id} pkg={pkg} index={i} />
        ))}
      </div>
      <p className="mt-10 text-center font-mono-grotesk text-[10.5px] tracking-widest uppercase text-white/35">
        50% Deposit · 50% On Launch · Payment Plans Available
      </p>
    </div>
  </section>
);

export default PricingCards;
